import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingApi } from '../services/api';
import { StatCard, Card, Badge, RefCode, AreaPill, Btn, Empty, Spinner, Modal } from '../components/UI';
import { filterByLastNDays, getLastNDaysRange, parseYmd } from '../utils/dateRange';

export default function DashboardPage() {
  const { user, isManagement } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const initialRange = getLastNDaysRange(6);
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const monthFromDate = new Date();
    monthFromDate.setDate(1);
    monthFromDate.setHours(0, 0, 0, 0);
    const monthToDate = new Date(monthFromDate.getFullYear(), monthFromDate.getMonth() + 1, 0);

    const monthFrom = `${monthFromDate.getFullYear()}-${String(monthFromDate.getMonth() + 1).padStart(2, '0')}-${String(monthFromDate.getDate()).padStart(2, '0')}`;
    const monthTo = `${monthToDate.getFullYear()}-${String(monthToDate.getMonth() + 1).padStart(2, '0')}-${String(monthToDate.getDate()).padStart(2, '0')}`;

    const recentBookingsPromise = (isManagement && user?.campusID)
      ? bookingApi.getCalendar(user.campusID, monthFrom, monthTo)
      : bookingApi.getMy();

    const statsPromise = isManagement
      ? bookingApi.getStats(user?.campusID)
      : Promise.resolve({ data: { data: null } });

    Promise.all([
      statsPromise,
      recentBookingsPromise,
      isManagement ? bookingApi.getPending(user?.campusID) : Promise.resolve({ data: { data: [] } })
    ])
      .then(([s, b, p]) => {
        const recentBookingsRaw = (b.data.data || []).map((x) => ({
          ...x,
          eventAreaName: x.eventAreaName || x.eventArea,
          createdBy: x.createdBy || x.createdByName
        }));
        const recentBookings = [...recentBookingsRaw].sort((a, z) => {
          const aAt = new Date(a.submittedAt || a.createdAt || a.eventDate || 0).getTime();
          const zAt = new Date(z.submittedAt || z.createdAt || z.eventDate || 0).getTime();
          return zAt - aAt;
        });

        const localStats = {
          totalBookings: recentBookingsRaw.length,
          approvedBookings: recentBookingsRaw.filter((x) => (x.status || '').toLowerCase() === 'approved').length,
          pendingBookings: recentBookingsRaw.filter((x) => (x.status || '').toLowerCase() === 'pending').length,
          rejectedBookings: recentBookingsRaw.filter((x) => (x.status || '').toLowerCase() === 'rejected').length,
          totalUsers: 0,
          totalEventAreas: 0
        };
        const recentPending = filterByLastNDays(p.data.data || [], 6, 'submittedAt');
        setStats(isManagement ? (s.data.data || localStats) : localStats);
        setBookings(recentBookings);
        hydrateBookingAssets(recentBookings);
        setPending(recentPending);
      })
      .catch(() => {
        setStats({
          totalBookings: 0,
          approvedBookings: 0,
          pendingBookings: 0,
          rejectedBookings: 0,
          totalUsers: 0,
          totalEventAreas: 0
        });
        setBookings([]);
        setPending([]);
      })
      .finally(() => setLoading(false));
  }, [isManagement, user?.campusID]);

  async function hydrateBookingAssets(baseBookings) {
    const firstFew = (baseBookings || []).slice(0, 5);
    if (!firstFew.length) return;

    const results = await Promise.allSettled(
      firstFew.map((item) => {
        const id = item?.bookingID || item?.bookingId || item?.id;
        if (!id) return Promise.resolve(null);
        return bookingApi.getById(id);
      })
    );

    const assetsMap = new Map();
    results.forEach((res, idx) => {
      if (res.status !== 'fulfilled' || !res.value) return;
      const full = res.value?.data?.data;
      if (!full) return;
      const id = full.bookingID || full.bookingId || firstFew[idx]?.bookingID || firstFew[idx]?.bookingId || firstFew[idx]?.id;
      if (!id) return;
      assetsMap.set(id, full.assets || full.Assets || []);
    });

    if (!assetsMap.size) return;

    setBookings((prev) =>
      prev.map((b) => {
        const id = b?.bookingID || b?.bookingId || b?.id;
        if (!assetsMap.has(id)) return b;
        return { ...b, assets: assetsMap.get(id) };
      })
    );
  }

  async function openBookingDetails(booking) {
    setSelectedBooking(booking);
    setDetailLoading(true);
    setDetailError('');
    try {
      const bookingId = booking?.bookingID || booking?.bookingId || booking?.id;
      if (!bookingId) return;
      const res = await bookingApi.getById(bookingId);
      const full = res?.data?.data;
      if (full) {
        setSelectedBooking((prev) => ({
          ...(prev || {}),
          ...full,
          eventAreaName: full.eventAreaName || full.eventArea,
          createdBy: full.createdBy || full.createdByName
        }));
      }
    } catch (err) {
      setDetailError(err.response?.data?.message || 'Unable to load full Booking LE details.');
    } finally {
      setDetailLoading(false);
    }
  }

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  function inDateRange(item) {
    const raw = item?.submittedAt || item?.eventDate || item?.date;
    const itemDate = parseYmd(raw);
    if (!itemDate) return false;
    const start = parseYmd(fromDate);
    const end = parseYmd(toDate);
    if (start && itemDate < start) return false;
    if (end && itemDate > end) return false;
    return true;
  }

  const filteredRecent = bookings
    .filter((b) => statusFilter === 'All' || normalizeStatus(b.status) === normalizeStatus(statusFilter))
    .filter((b) => inDateRange(b))
    .filter((b) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (b.eventName || b.event || '').toLowerCase().includes(q)
        || (b.bookingReference || b.ref || '').toLowerCase().includes(q)
        || (b.eventAreaName || b.eventArea || b.area || '').toLowerCase().includes(q)
        || (b.createdBy || b.createdByName || b.by || '').toLowerCase().includes(q)
      );
    });

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12 }}>
        <Spinner size={28} />
        <span style={{ color: 'var(--text2)' }}>Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="page-anim">
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: 22, fontWeight: 700 }}>
          {greeting()}, {user?.fullName?.split(' ')[0]}!
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>
          {user?.campusName ? `${user.campusName} Campus - ` : ''}
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: '1.6rem' }} className="stats-grid">
        <StatCard label="Total Booking LE" value={stats?.totalBookings ?? 0} icon="T" color="blue" />
        <StatCard label="Approved" value={stats?.approvedBookings ?? 0} icon="A" color="green" />
        <StatCard label="Pending" value={stats?.pendingBookings ?? 0} icon="P" color="orange" />
        <StatCard label="Rejected" value={stats?.rejectedBookings ?? 0} icon="R" color="red" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.4rem' }} className="two-col-layout">
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: 15, fontWeight: 700 }}>Recent Booking LE</div>
            <Btn size="sm" onClick={() => navigate('/bookings')}>View all</Btn>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 13, minWidth: 130, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 13, minWidth: 130, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 13, border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', outline: 'none', background: 'white' }}
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search event/reference/area/user..."
              style={{ padding: '7px 12px', fontSize: 13, flex: 1, minWidth: 170, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
            />
          </div>

          {filteredRecent.length === 0 ? (
            <Empty icon="[]" text="No Booking LE yet" />
          ) : (
            <>
              <div className="dashboard-bookings-table" style={{ overflowX: 'auto', borderRadius: 'var(--radius)', border: '1.5px solid var(--border)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg2)' }}>
                      {['Reference', 'Event', 'Date', 'Area', 'Who Booked', 'Required Assets', 'Status', 'Action'].map((h) => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecent.slice(0, 5).map((b) => (
                      <tr key={b.bookingID || b.bookingId || b.id} style={{ cursor: 'pointer' }} onClick={() => openBookingDetails(b)}>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
                          <RefCode value={b.bookingReference || b.ref} />
                        </td>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', fontWeight: 600 }}>
                          {b.eventName || b.event}
                        </td>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
                          {b.eventDate || b.date}
                        </td>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
                          <AreaPill value={(b.eventAreaName || b.eventArea || b.area || '').replace('Multi-Purpose Hall', 'MPH')} />
                        </td>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', color: 'var(--text2)', fontWeight: 600 }}>
                          {b.createdBy || b.createdByName || b.by || '--'}
                        </td>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', color: 'var(--text2)', maxWidth: 220 }}>
                          {assetSummaryForRow(b)}
                        </td>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
                          <Badge status={b.status} />
                        </td>
                        <td style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
                          <Btn size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openBookingDetails(b); }}>
                            View Details
                          </Btn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="dashboard-bookings-mobile" style={{ display: 'none', flexDirection: 'column', gap: 10 }}>
                {filteredRecent.slice(0, 5).map((b) => (
                  <div key={`mobile-${b.bookingID || b.bookingId || b.id}`} style={{ border: '1.5px solid var(--border)', borderRadius: 10, padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <RefCode value={b.bookingReference || b.ref} />
                      <Badge status={b.status} />
                    </div>
                    <div style={{ fontWeight: 700, marginTop: 8, fontSize: 14 }}>{b.eventName || b.event}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                      {b.eventDate || b.date} | {(b.eventAreaName || b.eventArea || b.area || '').replace('Multi-Purpose Hall', 'MPH')}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                      {b.createdBy || b.createdByName || b.by || '--'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                      {assetSummaryForRow(b)}
                    </div>
                    <Btn size="sm" variant="ghost" style={{ marginTop: 8 }} onClick={() => openBookingDetails(b)}>
                      View Details
                    </Btn>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          {bookings.find((b) => b.status === 'Approved') && (
            <div className="next-approved-card" style={{ background: 'linear-gradient(135deg,#2E86C8 0%,#1a5f96 100%)', borderRadius: 'var(--radius)', padding: '1.4rem', border: 'none' }}>
              <div style={{ color: 'rgba(255,255,255,.7)', fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Next Approved Event</div>
              {(() => {
                const b = bookings.find((x) => x.status === 'Approved');
                return (
                  <>
                    <div style={{ color: 'white', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{b.eventName || b.event}</div>
                    <div style={{ color: 'rgba(255,255,255,.8)', fontSize: 12, marginBottom: 4 }}>Date: {b.eventDate || b.date} | {b.startTime || b.s}-{b.endTime || b.e}</div>
                    <div style={{ color: 'rgba(255,255,255,.8)', fontSize: 12 }}>Area: {b.eventAreaName || b.eventArea || b.area}</div>
                  </>
                );
              })()}
            </div>
          )}

          {isManagement && (
            <Card>
              <div style={{ fontFamily: 'var(--font-head)', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Needs Approval ({pending.length})</div>
              {pending.slice(0, 2).map((b) => (
                <div key={b.bookingID || b.id} onClick={() => navigate('/approvals')} style={{ padding: 10, background: 'var(--orange-light)', borderRadius: 'var(--radius-sm)', marginBottom: 8, cursor: 'pointer' }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{b.eventName || b.event}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{b.createdBy || b.by} | {b.eventDate || b.date}</div>
                </div>
              ))}
              {pending.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '12px 0' }}>All caught up.</div>}
              <Btn variant="primary" size="sm" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }} onClick={() => navigate('/approvals')}>
                Review All
              </Btn>
            </Card>
          )}

          <Card className="dashboard-cta-card" style={{ background: 'var(--green-light)', border: '1.5px solid #b8e0cb' }}>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Booking LE</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>Reserve MPH, Conference Room, and more</div>
            <Btn variant="green" size="sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/new-booking')}>
              + Booking LE
            </Btn>
          </Card>
        </div>
      </div>

      {selectedBooking && (
        <Modal title="Booking LE Details" onClose={() => setSelectedBooking(null)}>
          {detailLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '1rem 0' }}>
              <Spinner size={18} />
              <span style={{ color: 'var(--text2)', fontSize: 13 }}>Loading Booking LE details...</span>
            </div>
          ) : (
            <DashboardBookingDetail b={selectedBooking} />
          )}
          {detailError && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--red)' }}>{detailError}</div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: '1rem', justifyContent: 'flex-end' }}>
            {isManagement && selectedBooking.status === 'Pending' && (
              <Btn variant="primary" onClick={() => { setSelectedBooking(null); navigate('/approvals'); }}>
                Review in Approvals
              </Btn>
            )}
            <Btn variant="ghost" onClick={() => setSelectedBooking(null)}>Close</Btn>
          </div>
        </Modal>
      )}

      <style>{`
        @media(max-width:900px){
          .stats-grid{grid-template-columns:1fr 1fr!important}
          .two-col-layout{grid-template-columns:1fr!important}
        }
        @media(max-width:768px){
          .dashboard-bookings-table{display:none!important}
          .dashboard-bookings-mobile{display:flex!important}
          .next-approved-card,.dashboard-cta-card{padding:1rem!important}
          .next-approved-card div{overflow-wrap:anywhere}
        }
        @media(max-width:480px){
          .stats-grid{grid-template-columns:1fr 1fr!important}
        }
      `}</style>
    </div>
  );
}

function assetSummaryForRow(booking) {
  const assets = booking?.assets || booking?.Assets || [];
  if (!assets.length) return '--';
  return assets
    .map((a) => {
      const name = (a.assetName || `Asset #${a.assetID || a.assetId || ''}`).trim();
      const qty = a.quantityRequested ?? a.quantity ?? 0;
      return `${name} x ${qty}`;
    })
    .join(', ');
}

function DashboardBookingDetail({ b }) {
  const assets = b?.assets || b?.Assets || [];
  const rows = [
    ['Reference', b.bookingReference || b.ref],
    ['Status', b.status],
    ['Event Name', b.eventName || b.event],
    ['Campus', b.campusName || b.campus || '--'],
    ['Event Area', b.eventAreaName || b.eventArea || b.area || '--'],
    ['Date', b.eventDate || b.date || '--'],
    ['Time', `${b.startTime || b.s || '--:--'} - ${b.endTime || b.e || '--:--'}`],
    ['Slots', b.totalSlots || b.slots || '--'],
    ['Who Booked', b.createdBy || b.createdByName || b.by || '--'],
    ['Attendees', b.expectedAttendees || b.att || '--'],
    ['Purpose', b.purpose || '--'],
    ['Remarks', b.reviewRemarks || '--']
  ];

  return (
    <>
      <div className="dashboard-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ padding: '10px', background: 'var(--bg2)', borderRadius: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>
              {label}
            </div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {label === 'Status' ? <Badge status={value} /> : value}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, padding: 10, background: 'var(--bg2)', borderRadius: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 6 }}>
          Required Assets
        </div>
        {assets.length === 0 ? (
          <div style={{ fontWeight: 600, fontSize: 13 }}>No assets requested</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {assets.map((a, i) => (
              <div key={`${a.assetID || a.assetId || i}-${a.assetName || 'asset'}`} style={{ fontWeight: 600, fontSize: 13 }}>
                {a.assetName || `Asset #${a.assetID || a.assetId}`} x {a.quantityRequested ?? a.quantity ?? 0}
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        @media(max-width:640px){
          .dashboard-detail-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </>
  );
}

const DEMO_BOOKINGS = [
  { id: 1, bookingReference: 'CHW-20260601-0001', eventName: 'Annual Sports Day', eventAreaName: 'Multi-Purpose Hall', eventDate: '2026-06-10', startTime: '08:00', endTime: '11:00', status: 'Approved', createdBy: 'Priya Sharma' },
  { id: 2, bookingReference: 'CHW-20260601-0002', eventName: 'Science Exhibition', eventAreaName: 'Conference Room A', eventDate: '2026-06-12', startTime: '09:00', endTime: '12:00', status: 'Pending', createdBy: 'Rahul Mehta' },
  { id: 3, bookingReference: 'HJW-20260601-0001', eventName: 'Parent-Teacher Meet', eventAreaName: 'Seminar Hall', eventDate: '2026-06-15', startTime: '14:00', endTime: '17:00', status: 'Pending', createdBy: 'Anjali Patel' },
  { id: 4, bookingReference: 'CHW-20260601-0003', eventName: 'Robotics Workshop', eventAreaName: 'Computer Lab', eventDate: '2026-06-18', startTime: '10:00', endTime: '13:00', status: 'Approved', createdBy: 'Suresh Kumar' },
  { id: 5, bookingReference: 'CHW-20260601-0004', eventName: 'Staff Meeting', eventAreaName: 'Conference Room B', eventDate: '2026-06-20', startTime: '15:00', endTime: '16:00', status: 'Rejected', createdBy: 'Meena Singh' },
  { id: 6, bookingReference: 'HJW-20260601-0002', eventName: 'Cultural Programme', eventAreaName: 'Multi-Purpose Hall', eventDate: '2026-06-25', startTime: '16:00', endTime: '20:00', status: 'Pending', createdBy: 'Dev Rathi' }
];



function normalizeStatus(status) {
  return String(status || '').trim().toLowerCase();
}

